import { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import { VolumeX, X, Sparkles, Clock, Mail } from "lucide-react";
import { API } from "@/App";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { formatNextRun } from "@/lib/format";

const DISMISS_KEY = "mute_digest_dismissed_at";
const DISMISS_TTL_MS = 24 * 60 * 60 * 1000; // 24h

/**
 * Weekly mute-digest banner.
 * - Fetches GET /api/mute-digest/week
 * - Shows for symbols muted ≥2× in last 7d
 * - Lets the user "Make permanent" (POST /api/hidden-symbols, hours=720)
 *   or dismiss for 24h (sessionStorage flag)
 */
export default function MuteDigestBanner({ isAuthenticated, authHeaders, onMakePermanent }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [dismissed, setDismissed] = useState(false);
  const [nextRun, setNextRun] = useState(null);
  const [previewing, setPreviewing] = useState(false);

  const fetchDigest = useCallback(async () => {
    if (!isAuthenticated) return;
    setLoading(true);
    try {
      const headers = authHeaders();
      const [digestRes, schedRes] = await Promise.allSettled([
        axios.get(`${API}/mute-digest/week`, { headers }),
        axios.get(`${API}/mute-digest/schedule-info`, { headers }),
      ]);
      if (digestRes.status === "fulfilled") setItems(digestRes.value.data?.items || []);
      if (schedRes.status === "fulfilled") setNextRun(schedRes.value.data?.next_run_time || null);
    } catch (error) {
      console.error("Mute digest fetch failed:", error);
    } finally {
      setLoading(false);
    }
  }, [isAuthenticated, authHeaders]);

  useEffect(() => {
    // Respect dismissal for 24h via sessionStorage
    const ts = sessionStorage.getItem(DISMISS_KEY);
    if (ts && Date.now() - parseInt(ts, 10) < DISMISS_TTL_MS) {
      setDismissed(true);
      return;
    }
    fetchDigest();
  }, [fetchDigest]);

  const handleDismiss = () => {
    sessionStorage.setItem(DISMISS_KEY, String(Date.now()));
    setDismissed(true);
  };

  const handleMakePermanent = async (symbol) => {
    try {
      await axios.post(
        `${API}/hidden-symbols`,
        { symbol, hours: 720 }, // 30 days
        { headers: authHeaders() }
      );
      setItems((prev) => prev.filter((i) => i.symbol !== symbol));
      toast.success(`${symbol} muted for 30 days`);
      if (onMakePermanent) onMakePermanent(symbol);
    } catch (err) {
      toast.error(`Couldn't mute ${symbol}`);
    }
  };

  const handleEmailPreview = async () => {
    setPreviewing(true);
    try {
      const res = await axios.post(`${API}/mute-digest/email-test`, {}, { headers: authHeaders() });
      const status = res.data?.status;
      if (status === "sent") {
        toast.success(`Preview emailed to ${res.data.recipient}`);
      } else if (status === "skipped") {
        toast.info("Email not configured yet — admin must add a Resend key");
      } else if (status === "empty") {
        toast.info("Nothing to email this week");
      } else if (status === "error") {
        toast.error(res.data?.reason || "Email send failed");
      } else {
        toast.success("Preview triggered");
      }
    } catch (err) {
      toast.error("Couldn't send preview");
    } finally {
      setPreviewing(false);
    }
  };

  if (loading || dismissed || items.length === 0) return null;

  return (
    <AnimatePresence>
      <motion.div
        data-testid="mute-digest-banner"
        initial={{ opacity: 0, y: -10 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: -10 }}
        className="glass-effect mb-4 p-4 rounded-xl border border-[#F59E0B]/30 bg-gradient-to-r from-[#F59E0B]/5 to-transparent"
      >
        <div className="flex items-start gap-4">
          <div className="shrink-0 mt-1">
            <div className="w-10 h-10 rounded-full bg-[#F59E0B]/20 flex items-center justify-center">
              <VolumeX className="w-5 h-5 text-[#F59E0B]" />
            </div>
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="text-sm font-bold text-white" style={{ fontFamily: "Unbounded" }}>
                THIS WEEK YOU MUTED
              </span>
              <Sparkles className="w-3.5 h-3.5 text-[#F59E0B]" />
            </div>
            <p className="text-xs text-gray-400 mt-1 mb-3">
              Want to silence them for good? Each "Make permanent" mutes for 30 days.
            </p>
            <div className="flex flex-wrap gap-2">
              {items.map((it) => (
                <div
                  key={it.symbol}
                  data-testid={`digest-chip-${it.symbol}`}
                  className="flex items-center gap-2 glass-effect rounded-full pl-3 pr-1 py-1 border border-white/10"
                >
                  <span className="text-xs font-bold text-white mono">{it.symbol}</span>
                  <span className="text-[10px] text-gray-400">{it.mute_count}×</span>
                  <Button
                    data-testid={`digest-permanent-${it.symbol}`}
                    size="sm"
                    onClick={() => handleMakePermanent(it.symbol)}
                    className="h-6 px-2 text-[10px] bg-[#F59E0B]/20 text-[#F59E0B] hover:bg-[#F59E0B]/30 border border-[#F59E0B]/30 rounded-full"
                  >
                    Make permanent
                  </Button>
                </div>
              ))}
            </div>
            {nextRun && (
              <div className="flex items-center gap-3 mt-3 flex-wrap">
                <div
                  data-testid="digest-next-run"
                  className="flex items-center gap-1.5 text-[10px] text-gray-500"
                >
                  <Clock className="w-3 h-3" />
                  Next email digest <span className="text-gray-300">{formatNextRun(nextRun)}</span>
                </div>
                <button
                  data-testid="email-preview-btn"
                  onClick={handleEmailPreview}
                  disabled={previewing}
                  className="flex items-center gap-1 text-[10px] text-[#F59E0B] hover:text-[#fbbf24] disabled:opacity-50 transition-colors"
                >
                  <Mail className="w-3 h-3" />
                  {previewing ? "Sending..." : "Email me a preview"}
                </button>
              </div>
            )}
          </div>
          <button
            data-testid="dismiss-mute-digest"
            onClick={handleDismiss}
            className="shrink-0 p-1 rounded hover:bg-white/5 text-gray-500 hover:text-white transition-colors"
            aria-label="Dismiss"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      </motion.div>
    </AnimatePresence>
  );
}
